import {Button, Flex, Form, Modal, Select} from 'antd';
import React, {useEffect, useRef} from 'react';

import {drawerZIndex} from '../../../../constants/Common';
import type {IUserPeerCompany} from '../../../../store/companyStore/companyDetailsStore';

export interface PeerCompanyNameOption {
  value: string;
  label: string;
  entityId: number;
  website?: string;
}

interface AddPeerCompaniesModalProps {
  open: boolean;
  initialCompanies: IUserPeerCompany[];
  companies: PeerCompanyNameOption[];
  onClose: () => void;
  onSubmit: (companies: IUserPeerCompany[]) => void;
}

interface PeerCompaniesForm {
  companyNames: string[];
}

const AddPeerCompaniesModal: React.FC<AddPeerCompaniesModalProps> = ({
  open,
  initialCompanies,
  companies,
  onClose,
  onSubmit
}) => {
  const [form] = Form.useForm<PeerCompaniesForm>();
  const wasOpen = useRef(false);

  const toCompanies = (companyNames: string[]): IUserPeerCompany[] =>
    companyNames.map((companyName) => ({
      companyName,
      companyWebsite:
        companies.find((company) => company.value === companyName)?.website ??
        ''
    }));

  useEffect(() => {
    // Fill the form only when the modal opens, so a refetch triggered by a
    // selection change does not overwrite what the user is picking.
    if (open && !wasOpen.current) {
      form.setFieldsValue({
        companyNames: initialCompanies
          .map((company) => company.companyName)
          .filter((name) => name.trim() !== '')
      });
    }
    wasOpen.current = open;
  }, [form, initialCompanies, open]);

  return (
    <Modal
      title="Add your Peer companies"
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={720}
      destroyOnClose
      zIndex={drawerZIndex}
      className="peerCompaniesModal"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={({companyNames}: PeerCompaniesForm) => {
          onSubmit(toCompanies(companyNames));
        }}
      >
        <Form.Item
          name="companyNames"
          label="Company name"
          rules={[
            {
              required: true,
              type: 'array',
              min: 1,
              message: 'Enter the company name'
            }
          ]}
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Enter company name"
            options={companies}
            optionFilterProp="label"
            maxTagCount="responsive"
          />
        </Form.Item>
        <Flex justify="flex-end" style={{marginTop: 16}}>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};

export default AddPeerCompaniesModal;
